import axios from "axios";
import { env } from "../utils/env";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";

export type Scope = "all" | "individual" | "entity";

export type RiskLevel = "Low" | "Medium" | "High";

export interface Industry {
  id: number;
  name: string;
  code: string;
  risk_level: RiskLevel;
  active: boolean;
  updated_at: string;
  order: number | null;
  _type: string;
}

interface IndustryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Industry[];
}

const fetch = (authHeader: string, scope: Scope, activeOnly: boolean) => () => {
  const base = `${env.verifyServiceUrl}individual/sic_industry_risk_level/`;
  const url =
    scope === "all" ? `${base}?all=true` : `${base}?_type=${scope}&all=true`;

  return axios.get<IndustryResponse | Industry[]>(
    activeOnly ? `${url}&active=true` : url,
    { headers: { Authorization: authHeader } },
  );
};

export const useGetSICIndustries = (scope: Scope = "all", activeOnly = false) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["sic_industry_risk_level", scope, activeOnly],
    queryFn: fetch(authHeader, scope, activeOnly),
    retry: false,
    select: (response): Industry[] => {
      const data = response.data as IndustryResponse | Industry[];
      return Array.isArray(data) ? data : data.results ?? [];
    },
    onError: (err: any) => {
      const status = err?.response?.status ?? err?.request?.status;
      if (status === 500 || status === 503) navigate(ROUTE.SERVER_ERROR);
      if (status === 401) navigate(ROUTE.LOGOUT);
    },
  });
};
